import * as React from 'react';
import commonStrings from 'owa-strings/owa-mini-common-strings';
import getAdjustedIconClassName from 'owa-mini-icons/lib/utils/getAdjustedIconClassName';
import getEventDurationDisplayString from 'mini-common-utils/lib/utils/getEventDurationDisplayString';
import isStringNullOrWhiteSpace from 'owa-strings/utilities/isStringNullOrWhiteSpace';
import strings from 'owa-strings/owa-agenda-strings';
import TimeConstants from 'mini-datetime-utils/lib/dateValues/TimeConstants';
import { Button, ButtonType } from 'mini-button';
import { CalendarEvent } from 'mini-calendar-event-types';
import { EventCardCharm } from './EventCardCharm';
import { EventCardContentProps } from '../schema/EventCardContent.types';
import { formatUserTime } from 'owa-datetime';
import { getEffectiveCharmId } from 'mini-charms/lib/utils/getEffectiveCharmId';
import { isNullOrUndefined } from 'mini-common-utils/lib/utils/isNullOrUndefined';
import { MiniIcons } from 'owa-mini-icons';
import { store } from 'mini-theme/lib/store/store';

const styles = require('./EventCardContent.scss');
const classNames = require('classnames/bind').bind(styles);

function renderTeamsMeetingLink(
    item: CalendarEvent,
    setShowOverflow: React.Dispatch<React.SetStateAction<boolean>>
) {
    if (!item?.OnlineMeetingJoinUrl) {
        return null;
    }

    if (!item.IsUpcomingEvent(15 * TimeConstants.MillisecondsIn1Min) && !item.IsOngoingEvent) {
        return null;
    }

    return (
        <a
            href={item?.OnlineMeetingJoinUrl}
            target="_blank"
            className={styles.joinMeetingLink}
            tabIndex={-1}
            onFocus={() => setShowOverflow(true)}
            onBlur={() => setShowOverflow(false)}>
            <Button
                text={strings.agendaJoinButtonText}
                type={ButtonType.InlinePrimary}
                ariaLabel={strings.agendaJoinButtonText}
                isAriaHidden={false}
                className={classNames(styles.button)}
            />
        </a>
    );
}
/**
 * Component which renders a day view all day item
 */
const EventCardContent = (props: EventCardContentProps): JSX.Element => {
    const { item, additionalStyles, isCharmSmall } = props;

    const {
        charmStyles,
        titleStyles,
        locationStyles,
        locationIconStyles,
        timeAndDurationStyles,
        durationStyles,
    } = additionalStyles;

    const { Location, Subject, Start, End } = item;

    const [showOverflow, setShowOverflow] = React.useState(false);

    // Charm
    const isCharmPresent: boolean = !isNullOrUndefined(
        getEffectiveCharmId(item.CharmId, null /* TODO: Send parent calendar charm */, false)
    );
    const charmComponent =
        charmStyles && isCharmPresent ? (
            <div className={charmStyles}>
                <EventCardCharm item={item} isSmall={isCharmSmall} />
            </div>
        ) : null;

    // Subject
    const showSubject = !!titleStyles;
    const subjectComponent = showSubject ? (
        <div className={titleStyles}>{Subject || commonStrings.noSubjectText}</div>
    ) : null;

    // Duration
    const durationComponent = timeAndDurationStyles && (
        <div className={classNames(timeAndDurationStyles, styles.timeAndDuration)}>
            {formatUserTime(Start)}
            <div className={durationStyles}>
                ({getEventDurationDisplayString(Start, End, true /* useShortFormatOnly */)})
            </div>
        </div>
    );

    // Location
    const isLocationPresent: boolean =
        !!Location && !isStringNullOrWhiteSpace(Location.DisplayName);

    const locationIcon = locationIconStyles ? (
        <i
            className={classNames(
                styles.icon,
                locationIconStyles,
                getAdjustedIconClassName(MiniIcons.POI)
            )}
        />
    ) : null;

    const locationName =
        locationStyles && isLocationPresent ? (
            <div className={locationStyles}>{Location.DisplayName}</div>
        ) : null;

    const locationComponent = isLocationPresent ? (
        <div className={styles.itemLocationAndIcon}>
            {locationIcon}
            {locationName}
        </div>
    ) : null;

    const isButtonInFocus = () => {
        return showOverflow && store.darkMode;
    };

    return (
        <div
            className={classNames({
                [styles.itemContentWrapper]: !isButtonInFocus(),
                [styles.itemContentWrapperButtonFocus]: isButtonInFocus(),
            })}>
            <div className={styles.itemTextContent}>
                <div className={styles.itemCharmAndSubject}>
                    {charmComponent}
                    {subjectComponent}
                </div>
                {durationComponent}
                {locationComponent}
            </div>
            {renderTeamsMeetingLink(item, setShowOverflow)}
        </div>
    );
};

export default EventCardContent;
